/*
 * DaisyBee Arts — shared motion
 *
 * Two behaviours, both opt-in via data attributes so any section can use them:
 *   [data-db-reveal]            fade + rise once the element scrolls into view
 *   [data-db-parallax="0.15"]   drift vertically as the page scrolls
 *
 * Everything is a no-op when the visitor prefers reduced motion, and the
 * parallax work is batched into a single rAF tick shared by all elements.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------
   Reveal
   ------------------------------------------------------------------ */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
      )
    : null;

function registerReveals(root) {
  const targets = root.querySelectorAll('[data-db-reveal]:not(.is-revealed)');

  if (!revealObserver || reduceMotion.matches) {
    for (const el of targets) el.classList.add('is-revealed');
    return;
  }

  for (const el of targets) revealObserver.observe(el);
}

/* ------------------------------------------------------------------
   Parallax
   ------------------------------------------------------------------ */

const parallaxItems = new Set();
let ticking = false;

/**
 * Shift each registered element by a fraction of how far it sits from the
 * centre of the viewport. Clamped so nothing ever drifts far enough to open a
 * gap at the edge of its section.
 */
function updateParallax() {
  ticking = false;
  if (reduceMotion.matches) return;

  const viewportH = window.innerHeight;

  for (const { el, inner, speed, max } of parallaxItems) {
    const rect = el.getBoundingClientRect();

    // Skip anything comfortably off-screen.
    if (rect.bottom < -viewportH || rect.top > viewportH * 2) continue;

    const elCentre = rect.top + rect.height / 2;
    const fromCentre = (elCentre - viewportH / 2) / viewportH;
    const shift = Math.max(-max, Math.min(max, fromCentre * speed * -100));

    inner.style.setProperty('--db-shift', `${shift.toFixed(2)}px`);
  }
}

function requestParallax() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(updateParallax);
}

function registerParallax(root) {
  for (const el of root.querySelectorAll('[data-db-parallax]')) {
    if (el.dataset.dbParallaxBound === 'true') continue;

    const inner = el.querySelector('.db-parallax__inner') ?? el;
    const speed = parseFloat(el.dataset.dbParallax) || 0;
    const max = parseFloat(el.dataset.dbParallaxMax) || 40;

    if (speed === 0) continue;

    el.dataset.dbParallaxBound = 'true';
    parallaxItems.add({ el, inner, speed, max });
  }

  if (parallaxItems.size > 0) requestParallax();
}

/* ------------------------------------------------------------------
   Wiring
   ------------------------------------------------------------------ */

export function initDaisybeeMotion(root = document) {
  registerReveals(root);
  registerParallax(root);
}

window.addEventListener('scroll', requestParallax, { passive: true });
window.addEventListener('resize', requestParallax, { passive: true });

reduceMotion.addEventListener('change', () => {
  if (!reduceMotion.matches) {
    requestParallax();
    return;
  }
  // Motion turned off mid-session: settle everything back to its resting place.
  for (const { inner } of parallaxItems) inner.style.setProperty('--db-shift', '0px');
  for (const el of document.querySelectorAll('[data-db-reveal]')) el.classList.add('is-revealed');
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDaisybeeMotion());
} else {
  initDaisybeeMotion();
}

// The theme editor swaps section markup in and out without a page load.
document.addEventListener('shopify:section:load', (event) => {
  initDaisybeeMotion(event.target);
});

window.daisybeeMotion = { init: initDaisybeeMotion, refresh: requestParallax };
